import { Link } from "react-router-dom";
import { Wordmark } from "@/components/shieldx/Logo";
import { ShieldCheck, KeyRound, Siren, RotateCcw, FileClock, ArrowLeft } from "lucide-react";

const PILLARS = [
  { icon: ShieldCheck, t: "Protected Assets", d: "Organizations register and verify the digital systems they want protected." },
  { icon: KeyRound, t: "Authorization", d: "Every protected action is evaluated against identity, policy and risk before access." },
  { icon: Siren, t: "Detection & Alerts", d: "Unauthorized autonomous activity is detected, scored and escalated to the owner." },
  { icon: FileClock, t: "Auditability", d: "Every security-relevant action is recorded in a tamper-evident ledger." },
  { icon: RotateCcw, t: "Recovery", d: "Validated, trusted recovery — a compromised configuration is never blindly restored." },
];

export default function About() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      <header className="sticky top-0 z-50 border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link to="/"><Wordmark markSize={28} /></Link>
          <Link to="/login" className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500">Sign in</Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-20">
        <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200"><ArrowLeft className="h-4 w-4" /> Back</Link>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400/80">About ShieldX</p>
        <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-slate-50">The Digital Shield for the Autonomous Era</h1>
        <p className="mt-6 text-base leading-relaxed text-slate-400">
          ShieldX is an autonomous-era digital protection and authorization infrastructure developed by
          <span className="font-semibold text-slate-200"> Vijay Software Solutions Pvt Ltd</span>. It establishes a controlled
          protection boundary around registered digital assets and evaluates who or what is attempting access, what action is
          requested, and whether that action is authorized.
        </p>
        <p className="mt-4 text-base leading-relaxed text-slate-400">
          ShieldX is not a conventional cybersecurity product. It focuses on protected digital assets, authorization,
          autonomous activity, threat detection, alerts, blocking, containment, recovery and auditability — with human
          authority as the final boundary.
        </p>

        <div className="mt-12 grid gap-4 sm:grid-cols-2">
          {PILLARS.map((p) => (
            <div key={p.t} className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-700 bg-slate-800/60 text-blue-400"><p.icon className="h-5 w-5" /></span>
              <h3 className="mt-3 font-display text-base font-semibold text-slate-100">{p.t}</h3>
              <p className="mt-1 text-sm text-slate-400">{p.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-xl border border-slate-800 bg-slate-900/40 p-6">
          <p className="font-mono text-sm uppercase tracking-wider text-blue-400/90">Intelligence can evolve. Authority must remain controlled.</p>
        </div>
      </main>

      <footer className="border-t border-slate-800">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-2 px-6 py-8 sm:flex-row">
          <p className="text-xs text-slate-600">© 2026 ShieldX</p>
          <p className="font-mono text-[11px] text-slate-500">Powered by Vijay Software Solutions Pvt Ltd</p>
        </div>
      </footer>
    </div>
  );
}
